// Mulch project math: area × depth → cubic yards (bulk) and bag equivalents.
// Area dimensions follow the active unit system. Depth is always in inches.
// Landscape fabric, staples, and edging are fully quantified.
import { toFeet, rectArea, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface MulchArea {
  length: number
  width: number
}

// 'dimensions' = user enters one or more L × W beds (current default).
// 'area' = user already knows the total area and types it directly — useful
// for non-rectangular beds or pre-measured projects from a quote/plan.
export type MulchInputMode = 'dimensions' | 'area'

// Bag size is stored internally in cu ft (the unit the math operates in),
// but Canadian product labels use Litres just as often. `bagUnit` controls
// how the field is displayed and how user input is interpreted.
export type BagUnit = 'cuft' | 'L'

export interface MulchInputs {
  unit: UnitSystem
  inputMode: MulchInputMode
  areas: MulchArea[]
  totalArea: number        // sq ft (imperial) or m² (metric) — used in 'area' mode
  depthIn: number          // mulch depth, ALWAYS in inches
  bagCuFt: number          // bag size: ALWAYS stored in cu ft (math unit)
  bagUnit: BagUnit         // display unit only: 'cuft' or 'L'
  bagsHave: number         // reverse path: "I have N bags — what area will they cover?"
  wastePct: number
  needFabric: boolean
}

export interface MulchResult {
  areaSqft: number
  areaM2: number           // metric area, mirrors areaSqft
  cubicFeet: number        // raw, no waste
  cubicFeetTotal: number   // waste-adjusted cu ft (imperial volume display)
  cubicYards: number       // waste-adjusted (bulk-delivery convenience figure)
  cubicMeters: number      // waste-adjusted (metric volume display)
  bags: number             // bags of the selected bagCuFt size
  bags2CuFt: number        // always show 2 cu ft equivalent
  bags3CuFt: number        // always show 3 cu ft equivalent
  perimeterFt: number
  lines: AccessoryLine[]
}

export const DEFAULT_MULCH: MulchInputs = {
  unit: 'imperial',
  inputMode: 'dimensions',
  areas: [{ length: 10, width: 10 }],
  totalArea: 100,            // matches the default 10 × 10 = 100 sq ft
  depthIn: 3,
  bagCuFt: 2,
  bagUnit: 'cuft',
  bagsHave: 14,              // reverse path default — matches forward path's bag count for symmetry
  wastePct: 10,
  needFabric: true,
}

/**
 * Reverse calculation: given a stack of bags, how much area do they cover?
 * Returns area in sq ft (imperial) for the math layer; the page converts to
 * m² / square dimensions for display.
 *
 *   coverage (sq ft) = (bags × bagCuFt) ÷ (depthIn ÷ 12)
 *
 * Example: 14 bags × 2 cu ft = 28 cu ft. At 3 in deep (0.25 ft):
 *          28 ÷ 0.25 = 112 sq ft coverage.
 */
export interface MulchCoverageResult {
  totalCubicFeet: number    // raw bag volume
  totalLitres: number       // metric mirror
  coverageSqft: number      // imperial area at the chosen depth
  coverageM2: number        // metric area
  equivSquareFt: number     // side length of an equivalent square bed, ft
  equivSquareM: number      // side length, m
}

export function computeMulchCoverage(
  bags: number,
  bagCuFt: number,
  depthIn: number,
): MulchCoverageResult {
  const safeBags = Math.max(0, bags)
  const safeBag = Math.max(0.1, bagCuFt)
  const safeDepth = Math.max(0.1, depthIn)
  const totalCuFt = safeBags * safeBag
  const depthFt = safeDepth / 12
  const coverageSqft = totalCuFt / depthFt
  return {
    totalCubicFeet: round(totalCuFt, 1),
    totalLitres: round(totalCuFt * 28.3168466, 1),
    coverageSqft: round(coverageSqft, 0),
    coverageM2: round(coverageSqft * 0.09290304, 1),
    equivSquareFt: round(Math.sqrt(coverageSqft), 1),
    equivSquareM: round(Math.sqrt(coverageSqft * 0.09290304), 2),
  }
}

const FABRIC_SQFT_PER_ROLL = 150   // 3 ft × 50 ft roll
const STAPLE_PER_BAG = 75
const EDGING_ROLL_FT = 20

export function computeMulch(i: MulchInputs, locale: Locale = 'en'): MulchResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  let areaSqft = 0
  let perimeterFt = 0

  if (i.inputMode === 'area') {
    // Direct area entry — convert m² → sq ft if the user is in metric mode.
    // Perimeter is unknown, so edging defaults to 0 (user can still pick a
    // length for it manually via the option below, but we won't auto-quote).
    areaSqft = i.unit === 'metric' ? Math.max(0, i.totalArea) * 10.7639104 : Math.max(0, i.totalArea)
    perimeterFt = 0
  } else {
    for (const a of i.areas ?? []) {
      const lFt = toFeet(a.length, i.unit)
      const wFt = toFeet(a.width, i.unit)
      areaSqft += rectArea(lFt, wFt)
      perimeterFt += 2 * (Math.max(0, lFt) + Math.max(0, wFt))
    }
  }

  const depthFt = Math.max(0, i.depthIn) / 12
  const cubicFeet = areaSqft * depthFt
  const withWaste = applyWaste(cubicFeet, i.wastePct)
  const cubicYards = withWaste / 27

  const bagCuFt = Math.max(0.5, i.bagCuFt)
  const bags = cubicFeet > 0 ? ceilTo(withWaste / bagCuFt, 1) : 0
  const bags2CuFt = cubicFeet > 0 ? ceilTo(withWaste / 2, 1) : 0
  const bags3CuFt = cubicFeet > 0 ? ceilTo(withWaste / 3, 1) : 0

  const fabricRolls = i.needFabric && areaSqft > 0
    ? Math.max(1, ceilTo(areaSqft / FABRIC_SQFT_PER_ROLL, 1))
    : 0

  // 1 staple per 2 sq ft of fabric field area
  const stapleBags = i.needFabric && areaSqft > 0
    ? Math.max(1, ceilTo((areaSqft / 2) / STAPLE_PER_BAG, 1))
    : 0

  const edgingRolls = perimeterFt > 0 ? ceilTo(perimeterFt / EDGING_ROLL_FT, 1) : 0

  const lines: AccessoryLine[] = []

  if (i.needFabric) {
    lines.push({
      item: 'landscape fabric weed barrier', label: tr('Landscape fabric', 'Membrane géotextile'), kind: 'consumable',
      qty: fabricRolls, unit: 'roll',
      note: tr(`3 × 50 ft rolls (${FABRIC_SQFT_PER_ROLL} sq ft each) · overlap seams by 6 in`, `Rouleaux de 3 × 50 pi (${FABRIC_SQFT_PER_ROLL} pi² chacun) · chevaucher les joints de 6 po`),
    })
    lines.push({
      item: 'landscape fabric staples pins', label: tr('Landscape staples', 'Agrafes de paysagement'), kind: 'consumable',
      qty: stapleBags, unit: 'bag',
      note: tr(`Bags of ${STAPLE_PER_BAG} · every 2 sq ft and along all seams`, `Sacs de ${STAPLE_PER_BAG} · tous les 2 pi² et le long de tous les joints`),
    })
  }

  lines.push({
    item: 'plastic landscape edging border', label: tr('Landscape edging', 'Bordure de paysagement'), kind: 'consumable',
    qty: edgingRolls, unit: 'roll',
    note: perimeterFt > 0
      ? tr(`${EDGING_ROLL_FT} ft rolls · keeps mulch in and grass out`, `Rouleaux de ${EDGING_ROLL_FT} pi · garde le paillis à l’intérieur et le gazon à l’extérieur`)
      : tr(`${EDGING_ROLL_FT} ft rolls · switch to Dimensions input for an auto estimate`, `Rouleaux de ${EDGING_ROLL_FT} pi · basculez à la saisie par Dimensions pour une estimation automatique`),
    optional: true,
  })

  lines.push({ item: 'garden rake mulch spreading', label: tr('Garden rake', 'Râteau de jardin'), kind: 'tool', note: tr('For spreading mulch to an even depth.', 'Pour étaler le paillis à une profondeur uniforme.') })
  lines.push({ item: 'garden wheelbarrow', label: tr('Wheelbarrow', 'Brouette'), kind: 'tool', optional: true })
  lines.push({ item: 'square garden shovel fork', label: tr('Square shovel or fork', 'Pelle carrée ou fourche'), kind: 'tool', optional: true })

  // Metric mirrors of the imperial results. 1 sq ft = 0.09290304 m²; 1 cu ft = 0.028316846592 m³.
  const areaM2 = areaSqft * 0.09290304
  const cubicMeters = withWaste * 0.028316846592

  return {
    areaSqft: round(areaSqft, 0),
    areaM2: round(areaM2, 1),
    cubicFeet: round(cubicFeet, 1),
    cubicFeetTotal: round(withWaste, 1),
    cubicYards: round(cubicYards, 2),
    cubicMeters: round(cubicMeters, 2),
    bags,
    bags2CuFt,
    bags3CuFt,
    perimeterFt: round(perimeterFt, 0),
    lines,
  }
}
